import React, { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import Stack from 'react-bootstrap/Stack';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import ResultBarcard from 'components/formElements/ResultBarcard';
import ResultCounterCard from 'components/formElements/ResultCounterCard';
import BarChart from 'components/PDFElements/BarChart';
import { calcAi360ChartData } from 'components/PDFElements/AI360Chart';
import IconLightning from 'assets/images/icon_lightning.svg';
import IconEuro from 'assets/images/icon_euro.svg';
import Logo from 'assets/images/E3DC_logo-light.svg';
import MapSelection from 'components/PDFElements/MapSelection';
import Compass from 'components/formElements/Compass';
import DevicesDummy from 'assets/images/devices_big_dummy.png';
import { fetchSimulationData, fetchConfiguratorData } from 'services/simulatorApiProxy';
import staticDeviceList from 'data/staticDeviceList';
import { parseHashQueryParams } from 'services/routingHelper'
import {
    sumZonesSquareMeter,
    sumObstaclesSquareMeter,
    sumSquareMeterUsable,
    sumTotal,
    privateConsumptionYearly,
    privateConsumptionPercentage,
    getInstalledKWp,
    annualSavings,
    annualSavingsOver20Years,
    annualFeedInRevenue,
    incomeOver20Years
} from 'services/calculationHelper';
import AI360Image from 'assets/images/ai360.png';
import 'assets/styles/PDF.scss';

/*
http://docker.oms.int:3000/forms/chromium/convert/url
url: https://16ca-94-31-73-130.ngrok-free.app/#/pdf
scale: 0.4
*/

// http://localhost:3000/#/pdf?id=81cfc45d-39d8-47a1-b6f6-4652f834de20-20250422-1745327037251

const PDFLayout = () => {
    const [userData, setUserData] = useState(null);
    const [resultData, setResultData] = useState(null);
    const [storageConfiguration, setStorageConfiguration] = useState(null);
    const [error, setError] = useState(null);

    const fetchSimulationAPIData = async (id, isDocker = false) => {
        // Problem local development: if we are in Gotenberg we can't use localhost as endpoint url
        const result = await fetchSimulationData(id, isDocker);
        return result;
    }

    const qrCodeURL = () => {
        const ORIGIN = process.env.REACT_APP_GOTENBERG_QRCODE_ORIGIN;
        return `${ORIGIN}QR?id=${resultData.id}`;
    }

    useEffect(() => {
        const query = parseHashQueryParams();
        if (!query.id) {
            const msg = 'Required parameter "id" is missing';
            console.error(msg);
            setError(msg);
            return;
        }

        fetchSimulationAPIData(query.id, query.isDocker ?? false)
            .then((fetchResult) => {
                if (!fetchResult) {
                    const msg = 'fetchResult missing in API response';
                    console.error(msg, fetchResult);
                    setError(msg);
                    return;
                }
                // console.log('result', fetchResult);
                setResultData(fetchResult);
            })
            .catch(err => {
                console.error('Error while fetching simulation data', err);
                setError(`Fetch error: ${err.message}`);
            });

        fetchConfiguratorData(query.id, query.isDocker ?? false)
            .then((configuratorConfig) => {
                if (!configuratorConfig) {
                    const msg = 'configuratorConfig missing in API response';
                    console.error(msg, fetchResult);
                    setError(msg);
                    return;
                }
                // console.log('configuratorConfig', configuratorConfig);
                setUserData(configuratorConfig);
            })
            .catch(err => {
                // console.error('Error while fetching configurator data', err);
                setError(`Fetch error: ${err.message}`);
            });
    }, []);

    const batteryCapacity = () => {                
        return parseFloat(resultData.battery.split(' ')[1]);
    }

    const deviceImage = (storage) => {        
        var device = Object.values(staticDeviceList).find(value => value.model === storage);        
        if (!device) {           
            device = staticDeviceList.XSERIE;                    
        }
        return device.image;
    }

    const Typo = (props) => {
        const styles = {};
        const classes = [];

        if (props.className) {
            classes.push(props.className);
        }

        if (props.fs) {
            styles.fontSize = props.fs;
        }
        if (props.lh) {
            styles.lineHeight = props.lh;
        }
        if (props.bold) {
            styles.fontWeight = 'bold';
        }
        if (props.light) {
            classes.push("colorLight");
        }
        if (props.dark) {
            classes.push("colorDark");
        }
        if (props.green) {
            classes.push("colorGreen");
        }
        if (props.color) {
            styles.color = props.color;
        }
        if (props.mt) {
            classes.push("mt-" + props.mt);
        }
        if (props.mb) {
            classes.push("mb-" + props.mb);
        }
        if (props.inline) {
            classes.push("inline");
        }

        if (props.align) {
            switch (props.align) {
                case 'left':
                    classes.push('text-start');
                    break;
                case 'center':
                    classes.push('text-center');
                    break;
                case 'right':
                    classes.push('text-end');
                    break;
            }
        }

        return (
            <div style={styles} className={classes.join(" ")}>{props.children}</div>
        )
    }

    const ChartCard = (e) => {
        return (
            <div className="ChartCard text-center" style={{ "borderColor": e.hightlight }}>
                <Typo dark fs="35px" bold lh="60px">{e.title}</Typo>
                <Typo green fs="40px" bold lh="67px" inline>{e.value}</Typo>
                <Typo light fs="40px" bold lh="67px" inline className="ms-2">{e.unit}</Typo>
            </div>
        );
    }

    const Header = () => {
        return (
            <div className="d-flex justify-content-between">
                <div><img src={Logo} width="300" alt="E3DC" /></div>
                <div>
                    <Stack direction="horizontal">
                        <div className="me-3">
                            <svg
                                width="85"
                                height="85"
                                viewBox="0 0 85 85"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg">
                                <circle cx="42.5" cy="42.5" r="42.5" fill="#E9EDEF" />
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M53.8198 47.3099C56.149 48.6042 58.4795 49.8988 60.8086 51.1932C61.8111 51.7499 62.2509 52.931 61.8562 54.0083C59.8522 59.4829 53.9832 62.3726 48.4608 60.3572C37.1527 56.2297 28.7708 47.8473 24.6429 36.5392C22.6273 31.0169 25.5171 25.1477 30.9917 23.1438C32.069 22.7491 33.2502 23.1889 33.8082 24.1914C35.1012 26.5206 36.3958 28.8512 37.6902 31.1803C38.2969 32.2729 38.1543 33.5699 37.3239 34.503C36.2363 35.7268 35.1489 36.9504 34.0613 38.173C36.3829 43.8262 41.1736 48.6172 46.827 50.9386C48.0495 49.8511 49.2731 48.7637 50.4969 47.6761C51.4314 46.8456 52.7273 46.703 53.8197 47.3098L53.8198 47.3099Z"
                                    fill="#5CC244" />
                            </svg>
                        </div>
                        <div>
                            <Typo green bold fs="38px" lh="38px">0541 9321 321 0</Typo>
                            <Typo light fs="20px">Mo-Do 8:00 - 17:00 Uhr und Fr 8:00 - 15:00 Uhr</Typo>
                        </div>
                    </Stack>
                </div>
                <div>
                    <Stack direction="horizontal">
                        <div className="me-3">
                            <svg
                                width="85"
                                height="85"
                                viewBox="0 0 85 85"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg">
                                <circle cx="42.5" cy="42.5" r="42.5" fill="#E9EDEF" />
                                <path
                                    d="M70.223 26.1936C66.3692 19.9703 60.5183 15.2538 53.6361 12.8231C46.7538 10.3923 39.2525 10.3923 32.3692 12.8231C25.4869 15.2544 19.6355 19.9709 15.7823 26.1936C12.6569 31.2371 11 37.0595 11 43C11 48.9405 12.6569 54.7633 15.7823 59.8064C19.6362 66.0297 25.487 70.7462 32.3692 73.1769C39.2515 75.6077 46.7528 75.6077 53.6361 73.1769C60.5185 70.7456 66.3698 66.0291 70.223 59.8064C73.3451 54.7619 75 48.94 75 43C75 37.06 73.3451 31.2377 70.223 26.1936ZM65.9388 55.8837H56.4087C57.023 52.6352 57.4123 49.3481 57.5748 46.0453H69.232C68.7944 49.5119 67.6747 52.8558 65.9388 55.8837ZM43.0032 69.47C41.1379 69.47 38.8648 66.5422 37.1743 61.7398H48.8321C47.1415 66.5422 44.8685 69.47 43.0032 69.47ZM35.4843 55.8837C34.8032 52.6441 34.3938 49.3535 34.2601 46.0453H51.7463C51.6316 49.3516 51.2418 52.6426 50.5807 55.8837H35.4843ZM20.0677 30.351H29.5978C28.9835 33.5995 28.5942 36.8866 28.4317 40.1894H16.7744C17.2121 36.7228 18.3318 33.3789 20.0677 30.351ZM43.0033 16.7646C44.8686 16.7646 47.1417 19.6925 48.8322 24.4948H37.1744C38.865 19.6925 41.138 16.7646 43.0033 16.7646ZM50.5222 30.351C51.2033 33.5905 51.6127 36.8812 51.7464 40.1894H34.2602C34.3749 36.8831 34.7647 33.5921 35.4258 30.351H50.5222ZM16.9488 46.0455H28.4315C28.5749 49.347 28.9447 52.6341 29.539 55.8839H20.0675C18.3926 52.8434 17.3327 49.4993 16.9488 46.0455ZM57.5754 40.1899C57.432 36.8884 57.0621 33.6013 56.4679 30.3515H65.998C67.6524 33.3965 68.6921 36.74 69.058 40.1899H57.5754ZM61.5971 24.4953H54.9812C54.3938 22.4762 53.6234 20.516 52.6788 18.6391C56.0004 19.9601 59.016 21.9537 61.5385 24.4953H61.5971ZM33.3857 18.6391C32.4416 20.516 31.6708 22.4762 31.0832 24.4953H24.4678C26.9902 21.9537 30.006 19.9601 33.3276 18.6391H33.3857ZM24.6426 61.7386H31.0253C31.6127 63.7577 32.3831 65.7179 33.3277 67.5948C30.0061 66.2738 26.9905 64.2802 24.468 61.7386H24.6426ZM52.854 67.5948C53.7981 65.7179 54.5689 63.7577 55.1564 61.7386H61.7138C59.1402 64.3008 56.063 66.2952 52.6789 67.5948H52.854Z"
                                    fill="#5CC244" />
                            </svg>
                        </div>
                        <Typo green fs="38px" bold>www.e3dc.com</Typo>
                    </Stack>
                </div>
            </div>
        )
    }

    const Field = (props) => (
        <div className="fieldBox">
            <div>{props.value}</div>
            <div className="suffix">{props.suffix}</div>
        </div>
    )

    const InfoLine = (props) => (
        <div className="infoLine">
            <div>{props.label}</div>
            <div>{props.value}</div>
        </div>
    )

    const Infobox = () => (
        <div className="infobox">
            <Typo fs="27px" lh="170%">
                {userData.contact.Firstname} {userData.contact.Lastname}<br />
                {userData?.Address?.Address?.split(",").map((part, index) => (
                    <React.Fragment key={index}>
                        {part}<br />
                    </React.Fragment>
                ))}
            </Typo>
            <hr />
            <Row className="mt-2">
                <Col>
                    <Typo bold fs="28px" lh="120%">Konfiguration bearbeiten oder online ansehen?</Typo>
                    <Typo light fs="27px" lh="128%" mt="3">Scannen Sie jetzt Ihren QR-Code.</Typo>
                </Col>
                <Col className="d-flex justify-content-end">
                    <QRCodeSVG value={qrCodeURL()} fgColor="#5CC244" bgColor="#E9EDEF" size="210" />
                </Col>
            </Row>
        </div>
    );

    const DynamicPage = (props) => {
        const classes = ['DynamicPage', props.className];

        return (
            <div className={classes.join(" ")} style={props.style}><Header />{props.children}</div>
        );
    }

    const Page = (props) => {
        const classes = ['Page', props.className];

        return (
            <div className={classes.join(" ")} style={props.style}><Header />{props.children}</div>
        );
    }

    if (error) {
        return (
            <div style={{ color: 'red', padding: '2rem' }}>
                <h2>Error generating PDF</h2>
                <p>{error}</p>
            </div>
        );
    }

    if (!userData || !resultData) {
        return <div>Loading...</div>
    }

    const privateConsumptionTotal = privateConsumptionYearly(
        sumTotal(resultData.houseConsumptionMonthly),
        sumTotal(resultData.gridSupplyMonthly)
    );

    const feedInTotal = sumTotal(resultData.feedInMonthly);
    const solarProductionTotal = sumTotal(resultData.solarProductionMonthly);

    const cards = [
        {
            title: "Jahresertrag",
            value: sumTotal(resultData.solarProductionMonthly).toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }),
            suffix: "kWh"
        }, {
            title: "Netzbezug",
            value: sumTotal(resultData.gridSupplyMonthly).toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }),
            suffix: "kWh"
        }, {
            title: "Eigen- verbrauchsquote",
            value: privateConsumptionPercentage(privateConsumptionTotal, solarProductionTotal),
            suffix: "%"
        }, {
            title: "Einsparung in 20 Jahren",
            value: annualSavingsOver20Years(privateConsumptionTotal, userData.Energycost, userData.EnergycostTwentyYearIncrease).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
            suffix: "€"
        }, {
            title: "Einspeisung",
            value: sumTotal(resultData.feedInMonthly).toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }),
            suffix: "kWh"
        }, {
            title: "Jahresverbrauch",
            value: sumTotal(resultData.houseConsumptionMonthly).toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }),
            suffix: "kWh"
        }, {
            title: "Einsparung pro Jahr",
            value: annualSavings(privateConsumptionTotal, userData.Energycost).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
            suffix: "€"
        }, {
            title: "Einspeisevergütung pro Jahr",
            value: annualFeedInRevenue(feedInTotal, userData.EnergyCompensation).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
            suffix: "€"
        }        
    ];

    if (userData?.Roof?.Zones?.length > 0) {
        cards.push({
            title: "Installierte Solarleistung",
            value: getInstalledKWp(userData.Roof.Zones).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
            suffix: "kWp"
        });
    }

    const ai360ChartData = calcAi360ChartData({
        cars: userData.Cars,
        electricityPrice: parseInt(userData.Energycost),
        totalConsumptionMonthly: resultData.totalConsumptionMonthly,
        gridSupplyMonthly: resultData.gridSupplyMonthly,
        autarky: parseInt(resultData.autarkyYear),
        batteryCapacity: parseFloat(getInstalledKWp(userData.Roof.Zones).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })),
        wallboxConsumption: 0,
        heatPumpConsumption: userData.HeatingPumpConsumption || 0,
        houseConsumption: userData.AnnualConsumption
    })

    const annualSavingsAI360 = sumTotal(ai360ChartData.rest_cost_with_ai_360_sum_monthly) - sumTotal(ai360ChartData.rest_cost_with_ai_360_sum_monthly_module3);
    const annualSavingsAI360Over20Years = annualSavingsAI360 * 20;

    return (
        <div className="PDFWrapper">
            <Page>

                <Row className="mt-5">
                    <Col>
                        <Typo fs="25px" light mt="4">HagerEnergy GmbH - Ursula-Flick-Straße 8 - 49076 Osnabrück</Typo>
                        <Typo fs="45px" dark bold mt="2">Hallo {userData.contact.Firstname} {userData.contact.Lastname},</Typo>
                        <Typo light fs="27px" mt="4">Im Rahmen dieser Simulation haben wir den voraussichtlichen Energieertrag Ihrer Photovoltaikanalage berechnet, basierend auf den eingegebenen Dachdaten und den aktuellen Wetterbedingungen. Wir zeigen Ihnen, wie viel Strom Ihre Anlage erzeugen wird und wie Netzunabhängig sie Ihre Eigenversorgung zukünftig mit E3/DC gestalten können.</Typo>
                    </Col>
                    <Col className="justify-content-end d-flex">
                        <Infobox />
                    </Col>
                </Row>
                <Row style={{
                    marginTop: "200px"
                }}>
                    <Col>
                        <Typo light fs="44px" lh="170%">Ihr individuelles</Typo>
                        <Typo bold fs="73px" lh="120%">Simulationsergebnis</Typo>
                        <Row className="mt-5">
                            <Col>
                                <ResultBarcard
                                    value={parseInt(resultData.autarkyYear)}
                                    title="Autarkie"
                                    description="&nbsp;"
                                    icon={IconLightning}
                                    gap="1"
                                />
                            </Col>
                            <Col>
                                <ResultBarcard
                                    value="100"
                                    textValue={incomeOver20Years(privateConsumptionTotal, userData.Energycost, feedInTotal, userData.EnergycostTwentyYearIncrease, userData.EnergyCompensation).toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                    suffix="€"
                                    title="Gesamtvorteil"
                                    description="in 20 Jahren"
                                    icon={IconEuro}
                                    gap="1" />                               
                            </Col>
                        </Row>
                    </Col>
                    <Col className="d-flex align-items-center">                        
                        <Row>
                            <div className="d-flex flex-row devices justify-content-center align-items-center">
                                <div><img style={{ width: '100%' }} src={deviceImage(resultData.storage)} /></div>
                                <div className="mt-4">
                                    <Typo light fs="27px" lh="170%">Unsere Empfehlung</Typo>
                                    <Typo bold fs="46px" lh="120%">{resultData.storage}</Typo>
                                    <Typo bold light fs="30px" lh="170%">{batteryCapacity(resultData.storage)} kWh (nutzbar)</Typo>
                                </div>
                            </div>
                        </Row>
                    </Col>
                </Row>

                <Row style={{
                    marginTop: "50px"
                }}>
                    <Col className="resultCounterCardWraper mt-4">
                        {
                        cards.map((card, index) => (<ResultCounterCard
                            className=''
                            key={index}
                            title={card.title}
                            value={card.value}
                            suffix={card.suffix} />))
                        }

                        <div className="resultCounterCard d-flex align-items-center px-4" style={{background: "none", textAlign: "left", paddingLeft: "10px", paddingRight: "10px"}}>                            
                            <div className='d-flex flex-row align-items-center mt-1'>                            
                                <Typo light fs="36px" lh="150%">Zusätzliches Sparpotential</Typo>
                                <img src={AI360Image} className='ms-1' style={{ height: '100px' }} />
                            </div>                            
                        </div>           

                        <ResultCounterCard 
                            className=''
                            key='ai_01'                            
                            title={<>
                                Einsparung mit<br />
                                AI 360°
                            </>}
                            value={annualSavingsAI360.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            suffix="€" />

                        <ResultCounterCard 
                            className=''
                            key='ai_02'                            
                            title={<>
                                Einsparung mit<br />
                                AI 360° in 20 Jahren
                            </>}
                            value={annualSavingsAI360Over20Years.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            suffix="€" />
                    </Col>
                </Row>
            </Page>

            {/* <Pagebreak/> */}

            <DynamicPage>
                <Row className="mt-5">
                    <Typo light fs="28px" lh="170%">{userData.Address.Address}</Typo>
                    <Typo bold fs="46px" lh="120%">Ihre Dachflächen</Typo>

                    {userData.Roof.Zones.map((zone, zindex) => {
                        return (
                            <Row
                                key={zindex}
                                className="d-flex justify-content-between align-items-center mapSelectionContainer mt-5">
                                <div><MapSelection center={userData.Address.LatLng} points={zone.Points} /></div>
                                <div>
                                    <Row className="d-flex justify-content-between align-items-center">
                                        <Col><Compass className="active mt-2" defaultDirection={zone.Direction} /></Col>
                                        <Col>
                                            <Typo fs="31px" lh="170%" bold>Nutzbare Fläche</Typo>
                                            <Field value={zone.SquareMeterUsable} suffix="m²" />
                                            <Typo fs="31px" lh="170%" mt="5" bold>Dachneigung</Typo>
                                            <Field value={zone.Tilt} suffix="Grad" />
                                        </Col>
                                    </Row>
                                </div>
                            </Row>
                        )
                    })}
                </Row>
                <Row className='mt-5'>
                    <Typo light fs="28px" lh="47px" color="#7B8492" mt="10">
                        {userData.Roof.Zones.length} {userData.Roof.Zones.length === 1 ? 'Dachfläche' : 'Dachflächen'}
                    </Typo>
                    <Typo bold fs="46px" lh="55px" color="#1C393D" mt="10">Gesamtauswertung</Typo>
                    <Stack className='mt-3'>
                        <InfoLine label="Gesamte Dachfläche" value={`${sumZonesSquareMeter(userData.Roof.Zones)} m²`} />
                        <InfoLine label="Gesamte Hindernisse" value={`${sumObstaclesSquareMeter(userData.Roof.Obstacles)} m²`} />
                        <InfoLine label="Installierte Solarleistung gesamt" value={`${getInstalledKWp(userData.Roof.Zones).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kWp`} />
                    </Stack>
                </Row>
            </DynamicPage>

            <Page>
                <Typo light fs="28px" lh="170%" mt="5">Prognostizierter Solarertrag und Netzbezug über ein Jahr</Typo>
                <Typo fs="46px" lh="120%" mt="2" mb="5" bold>Solarproduktion / Netzeinspeisung / Netzbezug</Typo>
                {/* Muss auf den richtigen Monat angepasst werden! */}
                <BarChart
                    solarProductionMonthly={resultData.solarProductionMonthly}
                    feedInMonthly={resultData.feedInMonthly}
                    gridSupplyMonthly={resultData.gridSupplyMonthly}
                />
                <Stack direction="horizontal" gap={5} className="mt-5 mb-5 justify-content-center">
                    <ChartCard title="Solarproduktion" value={sumTotal(resultData.solarProductionMonthly)} unit="kWh" hightlight="#00A4CF" />
                    <ChartCard title="Netzeinspeisung" value={sumTotal(resultData.feedInMonthly)} unit="kWh" hightlight="#03647F" />
                    <ChartCard title="Netzbezug" value={sumTotal(resultData.gridSupplyMonthly)} unit="kWh" hightlight="#5CC244" />
                </Stack>

                {/* <AI360Chart
                    chartData={ai360ChartData}
                />
                <Stack direction="horizontal" gap={5} className="mt-5 mb-5 justify-content-center">
                    <ChartCard title="Ohne E3DC" value={sumTotal(ai360ChartData.pre_cost_total_eur).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} unit="€" hightlight="#00A4CF" />
                    <ChartCard title="Fix Tarif (PV+Speicher)" value={sumTotal(ai360ChartData.rest_cost_with_ai_360_sum_monthly).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} unit="€" hightlight="#03647F" />
                    <ChartCard title="AI 360° + Modul 3 §14a EnWG°" value={sumTotal(ai360ChartData.rest_cost_with_ai_360_sum_monthly_module3).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} unit="€" hightlight="#5CC244" />
                </Stack> */}
            
                {/* <Typo light fs="28px" lh="170%" mt="5">Finden Sie Ihren passenden Dienstleister</Typo>
                <Typo fs="46px" lh="120%" mt="2" mb="5" bold>Zertifizierte Fachbetriebe in Ihrer Umgebung</Typo>

                {filteredLocations.map((location, index) => {
                    return (<PartnerRow key={index} location={location} />)
                })} */}
            </Page>

            <Page className="hasFooter">
                <Row>
                    <Typo light fs="28px" lh="170%" mt="5">Unabhängig leben - frei von Energiekosten</Typo>
                    <Typo fs="46px" lh="120%" mt="2" bold>Häufig gestellte Fragen</Typo>
                    <Row>
                        <Col>
                            <div className="mt-4">
                                <hr className="xl" />
                                <Typo bold fs="28px" lh="140%" mt="4">Wie lange hält ein E3/DC-Stromspeicher?</Typo>
                                <Typo light fs="20px" lh="140%" mt="2">Die hochwertigen Batteriemodule in den E3/DC-Systemen unterliegen während der gesamten Garantiezeit keiner Zyklenbegrenzung. Mit der umfassenden Leistungs- und Produktgarantie wird die volle Funktionalität des All In One-Speichersystems über einen Zeitraum von 10 Jahren sichergestellt.</Typo>
                            </div>
                            <div className="mt-4">
                                <hr className="xl" />
                                <Typo bold fs="28px" lh="140%" mt="4">Kann ich mein E3/DC-Hauskraftwerk später erweitern, z. B. mit zusätzlichem Speicher oder einer Wallbox?</Typo>
                                <Typo light fs="20px" lh="140%" mt="2">Ja, die E3/DC-Hauskraftwerke sind modular aufgebaut. Es ist möglich, sowohl die Speicherkapazität zu erweitern als auch Komponenten wie eine Wallbox zur Integration eines E-Autos nachzurüsten. Die Kompatibilität hängt jedoch vom jeweiligen Modell und Baujahr ab.</Typo>
                            </div>
                            <div className="mt-4">
                                <hr className="xl" />
                                <Typo bold fs="28px" lh="140%" mt="4">Wie funktioniert die Notstrom- oder Ersatzstromversorgung mit einem E3/DC-System bei Stromausfall?</Typo>
                                <Typo light fs="20px" lh="140%" mt="2">Das E3DC-Hauskraftwerk verfügt - je nach Modell - über eine integrierte Ersatzstromfunktion. Bei einem Stromausfall übernimmt Ihr Hauskraftwerk vollautomatisch die Energieversorgung im Gebäude. Die Versorgung im Ersatzstrom bzw. Inselbetrieb erfolgt aus der Speicherkapazität und ist Solar nachladbar aus Ihrer Photovoltaikanlage.</Typo>
                                <hr className="xl" />
                            </div>
                        </Col>
                        <Col className="justify-content-end d-flex align-items-end flex-column">
                            <Infobox />
                        </Col>
                    </Row>
                </Row>
                <div className="text-center devicesDummy"><img src={DevicesDummy} /></div>
                <div className="footer">
                    <Typo fs="27px" lh="170%" light align="center">Sie haben Fragen?</Typo>
                    <Typo fs="46px" lh="120%" bold align="center">Rufen Sie uns jetzt an</Typo>
                    <div className="d-flex justify-content-between mt-5">
                        <div>
                            <Stack direction="horizontal">
                                <div className="me-3">
                                    <svg
                                        width="85"
                                        height="85"
                                        viewBox="0 0 85 85"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="42.5" cy="42.5" r="42.5" fill="white" />
                                        <path
                                            d="M20.8251 26C20.324 26 19.8486 26.1049 19.4106 26.2776L41.0456 45.0973C41.9807 45.9105 42.9768 45.9114 43.9142 45.0973L65.589 26.2776C65.1514 26.1045 64.6756 26 64.1744 26H20.8248H20.8251ZM17.04 29.2722C17.0156 29.4453 17 29.6273 17 29.8074V55.1921C17 57.3014 18.7058 59 20.8252 59H64.1748C66.2937 59 68 57.302 68 55.1921V29.8074C68 29.6273 67.9844 29.4453 67.96 29.2718L46.4241 47.9737C44.1771 49.9246 40.782 49.9276 38.535 47.9737L17.04 29.2718L17.04 29.2722Z"
                                            fill="#5CC244" />
                                    </svg>
                                </div>
                                <div>
                                    <Typo green fs="38px" bold>anfrage@e3dc.com</Typo>
                                </div>
                            </Stack>
                        </div>
                        <div>
                            <Stack direction="horizontal">
                                <div className="me-3">
                                    <svg
                                        width="85"
                                        height="85"
                                        viewBox="0 0 85 85"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="42.5" cy="42.5" r="42.5" fill="white" />
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M53.8198 47.3099C56.149 48.6042 58.4795 49.8988 60.8086 51.1932C61.8111 51.7499 62.2509 52.931 61.8562 54.0083C59.8522 59.4829 53.9832 62.3726 48.4608 60.3572C37.1527 56.2297 28.7708 47.8473 24.6429 36.5392C22.6273 31.0169 25.5171 25.1477 30.9917 23.1438C32.069 22.7491 33.2502 23.1889 33.8082 24.1914C35.1012 26.5206 36.3958 28.8512 37.6902 31.1803C38.2969 32.2729 38.1543 33.5699 37.3239 34.503C36.2363 35.7268 35.1489 36.9504 34.0613 38.173C36.3829 43.8262 41.1736 48.6172 46.827 50.9386C48.0495 49.8511 49.2731 48.7637 50.4969 47.6761C51.4314 46.8456 52.7273 46.703 53.8197 47.3098L53.8198 47.3099Z"
                                            fill="#5CC244" />
                                    </svg>

                                </div>
                                <div>
                                    <Typo green bold fs="38px" lh="38px">0541 9321 321 0</Typo>
                                    <Typo light fs="20px">Mo-Do 8:00 - 17:00 Uhr und Fr 8:00 - 15:00 Uhr</Typo>
                                </div>
                            </Stack>
                        </div>
                        <div>
                            <Stack direction="horizontal">
                                <div className="me-3">
                                    <svg
                                        width="85"
                                        height="85"
                                        viewBox="0 0 85 85"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="42.5" cy="42.5" r="42.5" fill="white" />
                                        <path
                                            d="M70.223 26.1936C66.3692 19.9703 60.5183 15.2538 53.6361 12.8231C46.7538 10.3923 39.2525 10.3923 32.3692 12.8231C25.4869 15.2544 19.6355 19.9709 15.7823 26.1936C12.6569 31.2371 11 37.0595 11 43C11 48.9405 12.6569 54.7633 15.7823 59.8064C19.6362 66.0297 25.487 70.7462 32.3692 73.1769C39.2515 75.6077 46.7528 75.6077 53.6361 73.1769C60.5185 70.7456 66.3698 66.0291 70.223 59.8064C73.3451 54.7619 75 48.94 75 43C75 37.06 73.3451 31.2377 70.223 26.1936ZM65.9388 55.8837H56.4087C57.023 52.6352 57.4123 49.3481 57.5748 46.0453H69.232C68.7944 49.5119 67.6747 52.8558 65.9388 55.8837ZM43.0032 69.47C41.1379 69.47 38.8648 66.5422 37.1743 61.7398H48.8321C47.1415 66.5422 44.8685 69.47 43.0032 69.47ZM35.4843 55.8837C34.8032 52.6441 34.3938 49.3535 34.2601 46.0453H51.7463C51.6316 49.3516 51.2418 52.6426 50.5807 55.8837H35.4843ZM20.0677 30.351H29.5978C28.9835 33.5995 28.5942 36.8866 28.4317 40.1894H16.7744C17.2121 36.7228 18.3318 33.3789 20.0677 30.351ZM43.0033 16.7646C44.8686 16.7646 47.1417 19.6925 48.8322 24.4948H37.1744C38.865 19.6925 41.138 16.7646 43.0033 16.7646ZM50.5222 30.351C51.2033 33.5905 51.6127 36.8812 51.7464 40.1894H34.2602C34.3749 36.8831 34.7647 33.5921 35.4258 30.351H50.5222ZM16.9488 46.0455H28.4315C28.5749 49.347 28.9447 52.6341 29.539 55.8839H20.0675C18.3926 52.8434 17.3327 49.4993 16.9488 46.0455ZM57.5754 40.1899C57.432 36.8884 57.0621 33.6013 56.4679 30.3515H65.998C67.6524 33.3965 68.6921 36.74 69.058 40.1899H57.5754ZM61.5971 24.4953H54.9812C54.3938 22.4762 53.6234 20.516 52.6788 18.6391C56.0004 19.9601 59.016 21.9537 61.5385 24.4953H61.5971ZM33.3857 18.6391C32.4416 20.516 31.6708 22.4762 31.0832 24.4953H24.4678C26.9902 21.9537 30.006 19.9601 33.3276 18.6391H33.3857ZM24.6426 61.7386H31.0253C31.6127 63.7577 32.3831 65.7179 33.3277 67.5948C30.0061 66.2738 26.9905 64.2802 24.468 61.7386H24.6426ZM52.854 67.5948C53.7981 65.7179 54.5689 63.7577 55.1564 61.7386H61.7138C59.1402 64.3008 56.063 66.2952 52.6789 67.5948H52.854Z"
                                            fill="#5CC244" />
                                    </svg>

                                </div>
                                <Typo green fs="38px" bold>www.e3dc.com</Typo>
                            </Stack>
                        </div>
                    </div>
                    <Typo fs="27px" lh="170%" light align="center" className="mt-5">Finden Sie Ihren passenden Solateur</Typo>
                    <Typo green fs="36px" lh="120%" bold align="center"><a target="_blank" href="https://www.e3dc.com#partner-map-container">Zertifizierte Fachbetriebe in Ihrer Umgebung</a></Typo>
                    <div className="d-flex justify-content-between" style={{ marginTop: "70px" }}>
                        <Typo light fs="25px" lh="170%">HagerEnergy GmbH - Ursula-Flick-Straße 8 - 49076 Osnabrück<br />
                            Sitz der Gesellschaft: Osnabrück - Amtsgericht: Osnabrück HRB 213341<br />
                            Geschäftsführer: Dr. Andreas Piepenbrink, Sabine Busse<br />
                            USt-IdNr DE269804115<br />
                        </Typo>
                        <div><img src={Logo} width="300" alt="E3DC" /></div>
                    </div>
                </div>
            </Page>
        </div>
    );
}

export default PDFLayout;